export interface ActiveTab {
  /**
   * The alignment of the tab which currently can ONLY be 'left'
   * See EDI-431.
   */
  align: 'left';
  position: number;
}

export interface TabStop {
  align: 'left';
  position: number;
}

export interface ActiveParagraphTabs {
  first: ActiveTab[];
  rest: ActiveTab[];
}

/**
 * Takes a number of arguments describing the paragraph and generates the tab
 * positions for the paragraph.
 *
 * NOTE: This function does not care about units as long as they are consistent.
 * Currently, it is being used with px.
 *
 * There are potentially different tab positions for the first line and the
 * rest of the lines and this method returns both.
 */
export function buildActiveTabs({
  defaultTabWidth,
  indentationFirstLine,
  indentationLeft,
  innerWidth,
  tabStops,
}: {
  defaultTabWidth: number;
  /**
   * This number can be negative which represents a hanging indent.
   */
  indentationFirstLine: number;
  /**
   * The indentation of the left margin of the paragraph compared to the `0`
   * position on the ruler. This is an important distinction because the
   * indentationLeft can be -0.25, for example, and the first tab point may be
   * at 0, which is 0.25" from the left.
   */
  indentationLeft: number;
  /**
   * The width of the paragraph taking the `indentationLeft` into account.
   */
  innerWidth: number;
  tabStops: TabStop[];
}): ActiveParagraphTabs {
  const fullWidth = innerWidth + indentationLeft;

  function generateTabs(startIndentation: number, isFirstLine: boolean): ActiveTab[] {
    const activeTabs: ActiveTab[] = [];

    if (tabStops.length > 0) {
      /**
       * If there are tab stops, we give priority to them and if they exist we
       * also ignore the automatic tab at the hanging indent position.
       */
      tabStops.forEach(tab => {
        if (tab.position <= fullWidth) activeTabs.push({ align: 'left', position: tab.position });
      });
    } else if (isFirstLine) {
      /**
       * If there aren't tab stops, and there is a hanging indent, the first
       * tab position always aligns with the indentation for the rest of the
       * lines.
       */
      const isHangingIndent = indentationFirstLine < indentationLeft;
      if (isHangingIndent) activeTabs.push({ align: 'left', position: indentationLeft });
    }

    /**
     * Start at either the last tab stop position or the currentIndentation
     * for this line.
     */
    let startPosition = tabStops.reduce(
      (maxPosition, tab) => Math.max(maxPosition, tab.position),
      startIndentation
    );

    if (activeTabs.length > 0)
      startPosition = Math.max(activeTabs[activeTabs.length - 1].position, startPosition);

    // Adjust startPosition to the next multiple of defaultTabWidth
    startPosition = Math.ceil(startPosition / defaultTabWidth) * defaultTabWidth;

    /**
     * Ensure the first tab is not at:
     *
     * - the startIndentation (i.e. the left margin of the line)
     * - the same position as the last tab stop
     * - the same position as the hanging indent
     *
     * If we don't have this check, we end up with double tabs in some cases.
     */
    if (
      startPosition === startIndentation ||
      (activeTabs.length > 0 && activeTabs[activeTabs.length - 1].position === startPosition)
    ) {
      startPosition += defaultTabWidth;
    }

    /**
     * Keep adding default tabs until we reach the width of the line.
     *
     * A tab can be added right at the end of the line and that is okay and
     * matches the behavior in Microsoft Word.
     */
    while (startPosition <= fullWidth) {
      activeTabs.push({ align: 'left', position: startPosition });
      startPosition += defaultTabWidth;
    }

    /**
     * NOTE: We don't sort the tabs because they are already in the correct
     * order. The only way they might end up not in the right order is if the
     * tabStops are not in the right order.
     */
    return activeTabs;
  }

  return {
    first: generateTabs(indentationFirstLine, true),
    rest: generateTabs(indentationLeft, false),
  };
}

export function adjustActiveTabsRelativeToLine(
  activeTabs: ActiveParagraphTabs,
  options: { activeIndentationFirstLine: number; activeIndentationLeft: number }
): ActiveParagraphTabs {
  const { activeIndentationFirstLine, activeIndentationLeft } = options;

  return {
    /**
     * Here we do the same with the first line tabs as well. The adjustment is a
     * little confusing here because the `firstLineIndentAdjustedValPx` is
     * relative to the `indentationLeft` which is why we add them together.
     *
     * We can't just use the first line indent off the paragraph either because
     * there are some calculations that are done to the actual first line taking
     * into account suggested edits.
     */
    first: activeTabs.first.map(tab => ({
      align: tab.align,
      position: tab.position - (activeIndentationFirstLine + activeIndentationLeft),
    })),
    /**
     * We need to adjust these positions to be relative to the start of the line.
     *
     * If the lines are indented to the left by say 1", then we need to take 1"
     * off the tab positions.
     */
    rest: activeTabs.rest.map(tab => ({
      align: tab.align,
      position: tab.position - activeIndentationLeft,
    })),
  };
}
